import { useDataExplorerAppearance } from './appearanceContext';
import type { ReactNode } from 'react';

import { DataExplorerDisplayMedia } from './DataExplorerDisplayMedia';
import type {
  DataExplorerDisplayBadge,
  DataExplorerDisplayCardProps,
  DataExplorerDisplayMetadata,
  DataExplorerDisplayMediaItem,
} from './types';
import { resolveDataExplorerAppearance } from './theme';
import { cn } from './utils';

function isDisplayMedia<TSource>(
  value: unknown,
): value is
  | DataExplorerDisplayMediaItem<TSource>
  | Array<DataExplorerDisplayMediaItem<TSource>> {
  if (!value) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.every(
      (item) =>
        !!item &&
        typeof item === 'object' &&
        ('src' in item ||
          'thumbnail' in item ||
          'node' in item ||
          'kind' in item),
    );
  }
  return (
    typeof value === 'object' &&
    ('src' in value ||
      'thumbnail' in value ||
      'node' in value ||
      'kind' in value)
  );
}

function renderBadges(badges?: DataExplorerDisplayBadge[]) {
  if (!badges?.length) {
    return undefined;
  }

  return (
    <>
      {badges.map((badge, index) => (
        <span
          key={badge.id ?? index}
          title={badge.title}
          className={cn(
            'inline-flex max-w-full items-center rounded-full border border-border/60 px-2 py-0.5 text-xs font-medium',
            badge.tone === 'primary' && 'border-primary/30 bg-primary/10 text-primary',
            badge.tone === 'success' && 'border-[hsl(var(--success,142_72%_29%)_/_0.30)] bg-[hsl(var(--success,142_72%_29%)_/_0.10)] text-[hsl(var(--success,142_72%_29%))]',
            badge.tone === 'warning' && 'border-[hsl(var(--warning,45_93%_47%)_/_0.30)] bg-[hsl(var(--warning,45_93%_47%)_/_0.10)] text-[hsl(var(--warning-foreground,28_73%_26%))]',
            badge.tone === 'danger' && 'border-destructive/30 bg-destructive/10 text-destructive-text',
            badge.tone === 'muted' && 'bg-muted text-muted-foreground',
          )}
        >
          <span className="truncate">{badge.label}</span>
        </span>
      ))}
    </>
  );
}

function renderMetadata(metadata?: DataExplorerDisplayMetadata[]) {
  if (!metadata?.length) {
    return undefined;
  }

  return (
    <dl className="grid min-w-0 grid-cols-1 gap-2 text-xs sm:grid-cols-2">
      {metadata.map((entry, index) => (
        <div key={entry.id ?? index} className="min-w-0">
          {entry.label ? (
            <dt className="truncate text-muted-foreground" title={entry.title}>
              {entry.label}
            </dt>
          ) : null}
          <dd className="mt-0.5 min-w-0 truncate text-foreground">
            {entry.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function renderKeywords(keywords?: ReactNode[]) {
  if (!keywords?.length) {
    return undefined;
  }

  return (
    <div className="flex min-w-0 flex-wrap gap-1.5">
      {keywords.map((keyword, index) => (
        <span
          key={index}
          className="max-w-full truncate rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
        >
          {keyword}
        </span>
      ))}
    </div>
  );
}

export function DataExplorerDisplayCard<TSource = unknown>({
  item,
  title = item?.title,
  description = item?.description,
  badges = renderBadges(item?.badges),
  meta = item?.status,
  actions,
  media = item?.media,
  header,
  headerLeading,
  headerContent,
  overlay,
  footer,
  children,
  selected = item?.selected,
  disabled = item?.disabled,
  interactive,
  role,
  tabIndex,
  appearance,
  className,
  classNames,
  onClick,
  onKeyDown,
}: DataExplorerDisplayCardProps<TSource>) {
  const theme = resolveDataExplorerAppearance(
    useDataExplorerAppearance(appearance),
  );
  const resolvedInteractive = interactive ?? Boolean(onClick || onKeyDown);
  const hasDefaultHeader = Boolean(
    headerLeading ||
      headerContent ||
      title ||
      description ||
      badges ||
      meta ||
      actions,
  );
  const hasHeader = header !== undefined ? Boolean(header) : hasDefaultHeader;
  const renderedMedia = isDisplayMedia<TSource>(media) ? (
    <DataExplorerDisplayMedia media={media} fallbackTitle={title} />
  ) : (
    media
  );
  const defaultMetadata = renderMetadata(item?.metadata);
  const defaultKeywords = renderKeywords(item?.keywords);
  const resolvedChildren =
    children !== undefined
      ? children
      : defaultMetadata || defaultKeywords ? (
          <>
            {defaultMetadata}
            {defaultKeywords}
          </>
        )
      : undefined;
  const hasBody = Boolean(resolvedChildren);

  return (
    <article
      className={cn(
        theme.slots.cardRoot,
        resolvedInteractive && !disabled && theme.slots.cardInteractive,
        selected && theme.slots.cardSelected,
        disabled && theme.slots.cardDisabled,
        classNames?.root,
        className,
      )}
      aria-disabled={disabled || undefined}
      role={role}
      tabIndex={tabIndex}
      style={theme.styles.cardRoot}
      onClick={disabled ? undefined : onClick}
      onKeyDown={disabled ? undefined : onKeyDown}
    >
      {overlay ? (
        <div className={cn(classNames?.overlay)}>{overlay}</div>
      ) : null}

      {hasHeader ? (
        <div className={cn(theme.slots.cardHeader, classNames?.header)}>
          {header !== undefined ? (
            header
          ) : (
            <div
              className={cn(
                'flex min-w-0 items-start justify-between gap-3',
                classNames?.headerMain,
              )}
            >
              {headerLeading ? (
                <div
                  className={cn(
                    'flex shrink-0 items-center gap-2',
                    classNames?.headerLeading,
                  )}
                >
                  {headerLeading}
                </div>
              ) : null}

              <div className={cn('min-w-0 flex-1', classNames?.headerContent)}>
                {headerContent ?? (
                  <>
                    {title ? (
                      <div
                        className={cn(
                          'truncate text-sm font-semibold leading-none text-foreground',
                          classNames?.title,
                        )}
                      >
                        {title}
                      </div>
                    ) : null}
                    {description ? (
                      <div
                        className={cn(
                          'mt-1 min-w-0 text-sm text-muted-foreground [overflow-wrap:anywhere]',
                          classNames?.description,
                        )}
                      >
                        {description}
                      </div>
                    ) : null}
                    {badges ? (
                      <div
                        className={cn(
                          'mt-2 flex flex-wrap items-center gap-2',
                          classNames?.badges,
                        )}
                      >
                        {badges}
                      </div>
                    ) : null}
                  </>
                )}
              </div>

              {meta || actions ? (
                <div className="flex min-w-0 items-center gap-2">
                  {meta ? (
                    <div
                      className={cn(
                        'min-w-0 max-w-[12rem] truncate text-sm text-muted-foreground',
                        classNames?.meta,
                      )}
                    >
                      {meta}
                    </div>
                  ) : null}
                  {actions ? (
                    <div
                      className={cn(
                        'flex shrink-0 items-center gap-2',
                        classNames?.actions,
                      )}
                    >
                      {actions}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      {renderedMedia ? (
        <div className={cn(theme.slots.cardMedia, classNames?.media)}>
          {renderedMedia}
        </div>
      ) : null}

      {hasBody ? (
        <div className={cn('min-w-0 px-4 pb-4', classNames?.body)}>
          {resolvedChildren}
        </div>
      ) : null}

      {footer ? (
        <div
          className={cn(
            theme.slots.cardFooter,
            classNames?.footer,
          )}
        >
          {footer}
        </div>
      ) : null}
    </article>
  );
}
